import { Component, OnDestroy, OnInit, ViewEncapsulation, Inject } from '@angular/core';

import { MatDialogRef } from '@angular/material/dialog';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';

import { Subscription } from 'rxjs';
import { Config } from 'src/app/config';

import { CommonService } from 'src/app/services/common.service';
import { DrilldownFormsService, DynamicFormsCompleteEvent } from 'src/app/services/drilldown-forms.service';
import { ReportsService } from 'src/app/services/reports.service';
import { VrmFormsButton } from 'vrm-forms';

@Component({
  selector: 'popup-form',
  templateUrl: './popup-form.component.html',
  styleUrls: ['./popup-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PopupFormComponent implements OnInit, OnDestroy {
  loading: boolean;
  canCreateClaim: boolean;
  hasSaveAndCompleteButtons: boolean = false;
  saveButtonOptions: VrmFormsButton = null;
  saveAndCompleteButtonOptions: VrmFormsButton = null;
  formFlexPercentage: string;
  hidePrintButton: boolean = false;
  launchedFormName: string;
  environment = { vrmFormsLanguageAssetCdnRoot: Config.vrmFormsLanguageAssetCdnRoot };

  private subscriptions: { [key: string]: Subscription | null } = {
    submissionPrompt: null,
    completionSubmission: null,
    dialogClosed: null,
  };

  constructor(
    public cService: CommonService,
    public dfService: DrilldownFormsService,
    private rptService: ReportsService,
    public dialog: MatDialogRef<PopupFormComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { showGallery: boolean; galleryHasContent: boolean; canCreateClaim: boolean }
  ) {}

  public ngOnInit(): void {
    this.dialog.addPanelClass('popupForm');

    this.loading = this.data.showGallery && this.data.galleryHasContent;
    this.canCreateClaim = this.data.canCreateClaim;
    this.configureButtons();

    this.launchedFormName = this.dfService.currentFormStruct.metadata.name;

    this.subscriptions.dialogClosed = this.dialog.beforeClosed().subscribe({
      next: () => {
        this.dfService.closeFormPane();
      },
    });
  }

  public ngOnDestroy(): void {
    this.subscriptions.submissionPrompt?.unsubscribe();
    this.subscriptions.completionSubmission?.unsubscribe();
    this.subscriptions.dialogClosed?.unsubscribe();
  }

  private configureButtons(): void {
    if (this.dfService.currentFormStruct.metadata.name === 'DER') {
      this.hasSaveAndCompleteButtons = true;
      this.saveButtonOptions = {
        name: 'Save for later',
        translatedName: this.cService.getSingleTranslation('FORM.saveForLater', 'Save for later').toUpperCase(),
        textOnly: true,
      };
      this.saveAndCompleteButtonOptions = {
        name: 'Complete',
        translatedName: this.cService.getSingleTranslation('FORM.complete', 'Complete').toUpperCase(),
        textOnly: true,
      };
    }
  }

  public confirmSubmission(event: DynamicFormsCompleteEvent) {
    if (event && event.manuallyTriggered) {
      this.subscriptions.submissionPrompt = this.dfService.promptDERFormSubmissionConfirmation(event).subscribe({
        next: (markAsComplete: boolean) => {
          if (markAsComplete && event.formValid) {
            this.completeForm(event);
          }
        },
      });
    }
  }

  public saveForm(event: any) {
    if (
      this.dfService.launchedIntervention !== this.dfService.driverImprovementInterventionValue &&
      event &&
      event.formData
    ) {
      const formName = this.dfService.currentFormStruct.metadata.name;
      const formNameTranslated = this.cService.getSingleTranslation(
        `FORM.${this.dfService.launchedIntervention}`,
        formName
      );
      const employeePin = this.dfService.selectedDriver.pin;
      const securePin = this.dfService.selectedDriver.securePin ?? false;

      delete event.formComplete;
      event.submissionId = this.dfService.currentFormSubmissionId ? this.dfService.currentFormSubmissionId : '';

      this.dfService.saveDynamicForm(formName, employeePin, event, securePin).subscribe({
        next: (response: { submissionId: string }) => {
          if (!this.dfService.currentFormSubmissionId && response.submissionId) {
            this.dfService.currentFormSubmissionId = response.submissionId;
          }

          this.rptService.triggerReportDataChangedObservable();
          this.dialog.close();

          this.cService.showToast(`${formNameTranslated}: ${this.cService.readyTranslations.form.formSaved}`);
        },
        error: () => {
          this.cService.showToast(
            `${formNameTranslated}: ${this.cService.readyTranslations.form.failedToSaveFormData}`
          );
        },
      });
    }
  }

  private completeForm(event: DynamicFormsCompleteEvent): void {
    const formName = this.dfService.currentFormStruct.metadata.name;
    const employeePin = this.dfService.selectedDriver.pin;
    const securePin = this.dfService.selectedDriver.securePin ?? false;

    event.submissionId = this.dfService.currentFormSubmissionId ? this.dfService.currentFormSubmissionId : '';
    event.formComplete = true;

    this.subscriptions.completionSubmission = this.dfService
      .saveDynamicForm(formName, employeePin, event, securePin)
      .subscribe({
        next: (response) => {
          if (response.submissionId) {
            this.dfService.currentFormSubmissionId = response.submissionId;
          }

          if (this.rptService.currentReportMeta?.information?.refreshMenuNotificationCountType) {
            this.cService.triggerMenuBadgeCountUpdateObservable(
              this.rptService.currentReportMeta.information.refreshMenuNotificationCountType
            );
          }

          this.rptService.triggerReportDataChangedObservable();
          this.dialog.close();
        },

        error: () => {
          this.cService.showToast(`
          ${this.cService.readyTranslations.form[this.dfService.launchedIntervention]}:
          ${this.cService.readyTranslations.form.failedToSaveFormData}
        `);
        },
      });
  }

  onGalleryFinishedLoading(): void {
    this.loading = false;
  }

  onCreateClaim(): void {
    // TODO - To be implemented
    console.log('CREATE CLAIM FIRED');
  }
}
